import { Button, Icon } from '@design-system';
import type { TFunction } from 'next-i18next';
import { useTranslation } from 'next-i18next';

import { Modal } from '../../../Dialog/Modal';
import { Image } from '../../../Image';

const SECTION_IMAGES = {
  section1: 'https://images.avantarte.org/media/3BiS6DG5hzo2atKFlt6YAubT7Qx',
  section2: 'https://images.avantarte.org/media/3BiS6DA0In5j3RRUGP2JIKW9fjh',
} as const;

const SECTION_KEYS = ['section1', 'section2'] as const;

export interface GlazingGuideDialogProps {
  open: boolean;
  onClose: () => void;
}

/** Read an i18n array key as a plain `string[]` (ignores non-string entries). */
const getStringList = (t: TFunction, key: string): string[] => {
  const value = t(key, { returnObjects: true }) as unknown;
  return Array.isArray(value)
    ? (value as unknown[]).filter(
        (item): item is string => typeof item === 'string',
      )
    : [];
};

export const GlazingGuideDialog = ({
  onClose,
  open,
}: GlazingGuideDialogProps) => {
  const { t } = useTranslation('checkout');
  const title = t('framing.glazingGuide.title');

  return (
    <Modal
      dismissible
      onClose={onClose}
      open={open}
      overlayOtherDialogs
      title={title}
      verticalAlign="center"
    >
      <div className="relative flex max-h-[80dvh] w-full flex-col overflow-y-scroll rounded-t-lg bg-white no-scrollbar md:max-w-md md:overflow-y-auto md:rounded-lg">
        <div className="sticky top-0 z-30 flex w-full items-center justify-center bg-white p-6">
          <p className="text-center text-md font-bold">{title}</p>
          <div className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center">
            <Button
              hideLabel
              icon="ic_close"
              label="Close"
              onClick={onClose}
              size="sm"
              type="button"
              variant="transparent"
            />
          </div>
        </div>

        <div className="flex flex-col gap-6 p-6 pt-0">
          {SECTION_KEYS.map((sectionKey) => {
            const sectionTitle = t(`framing.glazingGuide.${sectionKey}.title`);
            const items = getStringList(
              t,
              `framing.glazingGuide.${sectionKey}.list`,
            );

            return (
              <div key={sectionKey} className="flex flex-col gap-3">
                <p className="text-pretty font-bold">{sectionTitle}</p>
                <ul>
                  {items.map((item) => (
                    <li key={item} className="flex gap-1 text-pretty">
                      <Icon name="ic_check" className="size-5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Image
                  alt={`Image for: ${sectionTitle}`}
                  size={{
                    sm: { ratio: '4/3', span: 12 },
                    md: { ratio: '4/3', span: 4 },
                    lg: { ratio: '4/3', span: 4 },
                  }}
                  src={SECTION_IMAGES[sectionKey]}
                />
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};
